import { produce } from 'immer';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import type {
  IRoomTimelineData,
  MatrixClient,
  MatrixEvent,
  Room,
  RoomEventHandlerMap,
  ReceiptType,
} from '$types/matrix-sdk';
import { RoomEvent, SyncState, EventType, ClientEvent, KnownMembership } from '$types/matrix-sdk';
import { useCallback, useEffect, useRef } from 'react';
import type { RoomToUnread, UnreadInfo, Unread } from '$types/matrix/room';
import { NotificationType } from '$types/matrix/room';
import { getAllParents } from '$utils/room/hierarchy';
import {
  getNotificationType,
  getUnreadInfo,
  getUnreadInfos,
  getUnreadInfosForRooms,
  isNotificationEvent,
} from '$utils/room/unread';
import { UnreadCountResolver } from '$utils/room/unreadResolver';
import { useSyncState } from '$hooks/useSyncState';
import { useRoomsNotificationPreferencesContext } from '$hooks/useRoomsNotificationPreferences';
import { useMatrixEvent } from '$hooks/useMatrixEvent';
import { getClientSyncDiagnostics, getSlidingSyncManager } from '$client/initMatrix';
import { mDirectAtom } from '$state/mDirectList';
import { roomToParentsAtom } from './roomToParents';

type RoomToUnreadAction =
  | {
      type: 'RESET';
      unreadInfos: UnreadInfo[];
    }
  | {
      type: 'PUT';
      unreadInfo: UnreadInfo;
    }
  | {
      type: 'DELETE';
      roomId: string;
    };

const unreadInfoToUnread = (unreadInfo: UnreadInfo): Unread => ({
  highlight: unreadInfo.highlight,
  total: unreadInfo.total,
  from: null,
  estimated: unreadInfo.estimated,
});

const putUnreadInfo = (
  roomToUnread: RoomToUnread,
  allParents: Set<string>,
  unreadInfo: UnreadInfo
) => {
  const oldUnread = roomToUnread.get(unreadInfo.roomId) ?? {
    highlight: 0,
    total: 0,
    from: null,
  };
  roomToUnread.set(unreadInfo.roomId, unreadInfoToUnread(unreadInfo));

  const newH = unreadInfo.highlight - oldUnread.highlight;
  const newT = unreadInfo.total - oldUnread.total;

  allParents.forEach((parentId) => {
    const oldParentUnread = roomToUnread.get(parentId) ?? {
      highlight: 0,
      total: 0,
      from: null,
    };
    roomToUnread.set(parentId, {
      highlight: (oldParentUnread.highlight += newH),
      total: (oldParentUnread.total += newT),
      from: new Set([...(oldParentUnread.from ?? []), unreadInfo.roomId]),
    });
  });
};

const deleteUnreadInfo = (roomToUnread: RoomToUnread, allParents: Set<string>, roomId: string) => {
  const oldUnread = roomToUnread.get(roomId);
  if (!oldUnread) return;
  roomToUnread.delete(roomId);

  allParents.forEach((parentId) => {
    const oldParentUnread = roomToUnread.get(parentId);
    if (!oldParentUnread) return;
    const newFrom = new Set(oldParentUnread.from ?? roomId);
    newFrom.delete(roomId);
    if (newFrom.size === 0) {
      roomToUnread.delete(parentId);
      return;
    }
    roomToUnread.set(parentId, {
      highlight: oldParentUnread.highlight - oldUnread.highlight,
      total: oldParentUnread.total - oldUnread.total,
      from: newFrom,
    });
  });
};

export const unreadEqual = (u1: Unread, u2: Unread): boolean => {
  const countEqual = u1.highlight === u2.highlight && u1.total === u2.total;

  if (!countEqual) return false;

  const f1 = u1.from;
  const f2 = u2.from;
  if (f1 === null && f2 === null) return true;
  if (f1 === null || f2 === null) return false;

  if (f1.size !== f2.size) return false;

  let fromEqual = true;
  f1?.forEach((item) => {
    if (!f2?.has(item)) {
      fromEqual = false;
    }
  });

  return fromEqual;
};

const baseRoomToUnread = atom(new Map());
export const roomToUnreadAtom = atom<RoomToUnread, [RoomToUnreadAction], undefined>(
  (get) => get(baseRoomToUnread),
  (get, set, action) => {
    const allParentsOf = (roomId: string): Set<string> =>
      getAllParents(get(roomToParentsAtom), roomId);

    if (action.type === 'RESET') {
      const draftRoomToUnread: RoomToUnread = new Map();
      action.unreadInfos.forEach((unreadInfo) => {
        putUnreadInfo(draftRoomToUnread, allParentsOf(unreadInfo.roomId), unreadInfo);
      });
      set(baseRoomToUnread, draftRoomToUnread);
      return;
    }
    if (action.type === 'PUT') {
      const { unreadInfo } = action;
      const currentUnread = get(baseRoomToUnread).get(unreadInfo.roomId);
      // Estimated results never replace counts resolved from read evidence.
      if (unreadInfo.estimated && currentUnread && currentUnread.total >= unreadInfo.total) {
        return;
      }
      if (unreadInfo.total <= 0 && unreadInfo.highlight <= 0) {
        if (get(baseRoomToUnread).has(unreadInfo.roomId)) {
          set(
            baseRoomToUnread,
            produce(get(baseRoomToUnread), (draftRoomToUnread) =>
              deleteUnreadInfo(
                draftRoomToUnread,
                allParentsOf(unreadInfo.roomId),
                unreadInfo.roomId
              )
            )
          );
        }
        return;
      }
      if (currentUnread && unreadEqual(currentUnread, unreadInfoToUnread(unreadInfo))) {
        // Do not update if unread data has not changed
        // like total & highlight
        return;
      }
      set(
        baseRoomToUnread,
        produce(get(baseRoomToUnread), (draftRoomToUnread) =>
          putUnreadInfo(draftRoomToUnread, allParentsOf(unreadInfo.roomId), unreadInfo)
        )
      );
      return;
    }
    if (action.type === 'DELETE' && get(baseRoomToUnread).has(action.roomId)) {
      set(
        baseRoomToUnread,
        produce(get(baseRoomToUnread), (draftRoomToUnread) =>
          deleteUnreadInfo(draftRoomToUnread, allParentsOf(action.roomId), action.roomId)
        )
      );
    }
  }
);

export const useBindRoomToUnreadAtom = (mx: MatrixClient, unreadAtom: typeof roomToUnreadAtom) => {
  const setUnreadAtom = useSetAtom(unreadAtom);
  const roomsNotificationPreferences = useRoomsNotificationPreferencesContext();
  const mDirects = useAtomValue(mDirectAtom);
  const roomToParents = useAtomValue(roomToParentsAtom);
  const shouldApplyUnreadFixup = useCallback(
    () => getClientSyncDiagnostics(mx).transport === 'sliding',
    [mx]
  );
  const resolverRef = useRef<UnreadCountResolver | null>(null);
  const mDirectsRef = useRef(mDirects);
  useEffect(() => {
    mDirectsRef.current = mDirects;
  }, [mDirects]);
  const publishUnreadActionRef = useRef<(action: RoomToUnreadAction) => void>(() => {});
  useEffect(() => {
    if (getClientSyncDiagnostics(mx).transport !== 'sliding') return undefined;
    const resolver = new UnreadCountResolver(mx, (room, unreadInfo) => {
      const emit = () => {
        const info =
          unreadInfo ?? getUnreadInfo(room, { applyFixup: true, mDirects: mDirectsRef.current });
        if (info.total > 0 || info.highlight > 0) {
          publishUnreadActionRef.current({ type: 'PUT', unreadInfo: info });
        } else {
          publishUnreadActionRef.current({ type: 'DELETE', roomId: room.roomId });
        }
      };
      const manager = getSlidingSyncManager(mx);
      if (manager?.isResponseProcessing()) {
        const unsubscribe = manager.subscribeToResponseSettled(() => {
          unsubscribe();
          emit();
        });
      } else {
        emit();
      }
    });
    resolverRef.current = resolver;
    return () => {
      resolver.dispose();
      resolverRef.current = null;
    };
  }, [mx]);
  const publishUnreadAction = useCallback(
    (action: RoomToUnreadAction) => {
      if (getClientSyncDiagnostics(mx).transport === 'sliding') {
        const infos =
          action.type === 'PUT'
            ? [action.unreadInfo]
            : action.type === 'RESET'
              ? action.unreadInfos
              : [];
        infos.forEach((info) => {
          if (info.estimated) resolverRef.current?.queue(info.roomId);
        });
        const manager = getSlidingSyncManager(mx);
        if (manager?.isResponseProcessing()) return;
      }
      setUnreadAtom(action);
    },
    [mx, setUnreadAtom]
  );
  const resetUnread = useCallback(
    () =>
      publishUnreadAction({
        type: 'RESET',
        unreadInfos: getUnreadInfos(mx, {
          applyFixup: shouldApplyUnreadFixup(),
          mDirects,
        }),
      }),
    [mx, publishUnreadAction, shouldApplyUnreadFixup, mDirects]
  );
  useEffect(() => {
    publishUnreadActionRef.current = publishUnreadAction;
  }, [publishUnreadAction]);

  useEffect(() => {
    const manager = getSlidingSyncManager(mx);
    if (!manager) return undefined;

    let cancelled = false;
    void manager.waitForSidebarCacheHydration().then((hydrated) => {
      if (!cancelled && hydrated) resetUnread();
    });
    return () => {
      cancelled = true;
    };
  }, [mx, resetUnread]);

  useEffect(() => {
    const manager = getSlidingSyncManager(mx);
    if (!manager) return undefined;
    return manager.subscribeToResponseSettled((dirtyRoomIds) => {
      if (dirtyRoomIds.size === 0) return;
      const { unread, deleted } = getUnreadInfosForRooms(mx, dirtyRoomIds, {
        applyFixup: true,
        mDirects,
      });
      for (const roomId of deleted) {
        publishUnreadAction({ type: 'DELETE', roomId });
      }
      for (const unreadInfo of unread) {
        publishUnreadAction({ type: 'PUT', unreadInfo });
      }
    });
  }, [mx, setUnreadAtom, publishUnreadAction, mDirects]);

  useEffect(() => {
    resetUnread();
  }, [resetUnread, roomToParents]);

  useSyncState(
    mx,
    useCallback(
      (state, prevState) => {
        if (
          (state === SyncState.Prepared && prevState === null) ||
          (state === SyncState.Syncing && prevState !== SyncState.Syncing)
        ) {
          resetUnread();
        }
      },
      [resetUnread]
    )
  );

  const handleTimelineEvent = useCallback(
    (
      mEvent: MatrixEvent,
      room: Room | undefined,
      toStartOfTimeline: boolean | undefined,
      removed: boolean,
      data: IRoomTimelineData
    ) => {
      if (!room || room.isSpaceRoom()) return;
      if (getNotificationType(mx, room.roomId) === NotificationType.Mute) {
        publishUnreadAction({
          type: 'DELETE',
          roomId: room.roomId,
        });
        return;
      }

      // Handle live events (new messages arriving in real-time)
      if (data.liveEvent && isNotificationEvent(mEvent)) {
        if (mEvent.getSender() === mx.getUserId()) return;
        const unreadInfo = getUnreadInfo(room, {
          applyFixup: shouldApplyUnreadFixup(),
          mDirects,
        });
        publishUnreadAction({
          type: 'PUT',
          unreadInfo,
        });
        return;
      }

      // Handle non-live events (initial sync/sliding sync timeline population)
      // For rooms without read receipts (unvisited in sliding sync), check if they need badges
      const userId = mx.getUserId();
      if (!data.liveEvent && userId && !room.getEventReadUpTo(userId)) {
        // Room has no read receipt - check if timeline activity warrants a badge
        const unreadInfo = getUnreadInfo(room, {
          applyFixup: shouldApplyUnreadFixup(),
          mDirects,
        });
        if (unreadInfo.total > 0 || unreadInfo.highlight > 0) {
          publishUnreadAction({
            type: 'PUT',
            unreadInfo,
          });
        }
      }
    },
    [mx, publishUnreadAction, shouldApplyUnreadFixup, mDirects]
  );

  useMatrixEvent(mx, RoomEvent.Timeline, handleTimelineEvent);

  const handleReceipt = useCallback(
    (mEvent: MatrixEvent, room: Room) => {
      const myUserId = mx.getUserId();
      if (!myUserId) return;
      if (room.isSpaceRoom()) return;
      const content = mEvent.getContent();

      const isMyReceipt = Object.keys(content).find((eventId) =>
        (Object.keys(content[eventId]) as ReceiptType[]).find(
          (receiptType) => content[eventId][receiptType][myUserId]
        )
      );
      if (isMyReceipt) {
        const unreadInfo = getUnreadInfo(room, {
          applyFixup: shouldApplyUnreadFixup(),
          mDirects,
        });
        if (unreadInfo.total === 0 && unreadInfo.highlight === 0) {
          publishUnreadAction({ type: 'DELETE', roomId: room.roomId });
          return;
        }
        publishUnreadAction({ type: 'PUT', unreadInfo });
      }
    },
    [mx, publishUnreadAction, shouldApplyUnreadFixup, mDirects]
  );

  useMatrixEvent(mx, RoomEvent.Receipt, handleReceipt);

  useEffect(() => {
    const roomListeners = new Map<Room, RoomEventHandlerMap[RoomEvent.UnreadNotifications]>();

    const bindRoom = (room: Room) => {
      if (roomListeners.has(room)) return;

      const handleUnreadNotifications: RoomEventHandlerMap[RoomEvent.UnreadNotifications] = () => {
        if (room.isSpaceRoom()) return;
        if (room.getMyMembership() !== (KnownMembership.Join as string)) return;

        const unreadInfo = getUnreadInfo(room, {
          // Counts are already updated before this event would recurse if true
          applyFixup: false,
          mDirects,
        });
        if (unreadInfo.total === 0 && unreadInfo.highlight === 0) {
          publishUnreadAction({ type: 'DELETE', roomId: room.roomId });
          return;
        }
        publishUnreadAction({ type: 'PUT', unreadInfo });
      };

      room.on(RoomEvent.UnreadNotifications, handleUnreadNotifications);
      roomListeners.set(room, handleUnreadNotifications);
    };

    mx.getRooms().forEach(bindRoom);
    mx.on(ClientEvent.Room, bindRoom);

    return () => {
      mx.removeListener(ClientEvent.Room, bindRoom);
      roomListeners.forEach((listener, room) => {
        room.removeListener(RoomEvent.UnreadNotifications, listener);
      });
    };
  }, [mx, publishUnreadAction, shouldApplyUnreadFixup, mDirects]);

  const handleRoomAccountData = useCallback(
    (mEvent: MatrixEvent, room: Room) => {
      if (room.isSpaceRoom()) return;
      if (mEvent.getType() !== (EventType.FullyRead as string)) return;

      const unreadInfo = getUnreadInfo(room, {
        applyFixup: shouldApplyUnreadFixup(),
        mDirects,
      });
      if (unreadInfo.total === 0 && unreadInfo.highlight === 0) {
        publishUnreadAction({ type: 'DELETE', roomId: room.roomId });
        return;
      }
      publishUnreadAction({ type: 'PUT', unreadInfo });
    },
    [publishUnreadAction, shouldApplyUnreadFixup, mDirects]
  );

  useMatrixEvent(mx, RoomEvent.AccountData, handleRoomAccountData);

  useEffect(() => {
    resetUnread();
  }, [resetUnread, roomsNotificationPreferences]);

  const handleMembershipChange = useCallback(
    (room: Room, membership: string) => {
      if (membership !== (KnownMembership.Join as string)) {
        publishUnreadAction({
          type: 'DELETE',
          roomId: room.roomId,
        });
      }
    },
    [publishUnreadAction]
  );

  useMatrixEvent(mx, RoomEvent.MyMembership, handleMembershipChange);

  // Seed badge state immediately when a room is first registered with the client
  // (e.g. after joining or receiving an invite that gets auto-accepted).
  // This avoids the brief window after refresh where badges are invisible until
  // the next timeline event arrives. Notifications are NOT triggered here —
  // ClientNonUIFeatures handles live notification pop-ups via its own listener.
  const handleRoomAdded = useCallback(
    (room: Room) => {
      if (room.isSpaceRoom() || room.getMyMembership() !== (KnownMembership.Join as string)) return;
      const unreadInfo = getUnreadInfo(room, {
        applyFixup: shouldApplyUnreadFixup(),
        mDirects,
      });
      if (unreadInfo.total > 0 || unreadInfo.highlight > 0) {
        publishUnreadAction({ type: 'PUT', unreadInfo });
      }
    },
    [publishUnreadAction, shouldApplyUnreadFixup, mDirects]
  );

  useMatrixEvent(mx, ClientEvent.Room, handleRoomAdded);
};
